import React from "react";

import Button, { ButtonTypes } from "../../../components/Button";
import { Skeleton } from "../../../components/Skeleton";
import { EmptyState } from "../../../components/States";
import { useResponsive } from "../../../hooks/useResponsive";
import { BREAKPOINTS } from "../../../utils/constants";
import { ObservedAt } from "../components/ObservedAt";
import { RefreshFailedNotice } from "../components/RefreshFailedNotice";
import { SectionUnavailable } from "../components/SectionUnavailable";
import {
  Caption,
  Card,
  PageContent,
  PageHeader,
  PageHeading,
  PageTitle,
} from "../styles";
import { plural } from "../utils/format";
import { ActivityList } from "./components/ActivityList";
import { ActivityPager } from "./components/ActivityPager";
import { ActivityToolbar } from "./components/ActivityToolbar";
import { useActivity } from "./hooks/useActivity";

export const ActivityPage: React.FC = () => {
  const {
    section,
    query,
    action,
    period,
    page,
    pageSize,
    records,
    total,
    isFiltered,
    changeQuery,
    changeAction,
    changePeriod,
    changePage,
    changePageSize,
    clearFilters,
    isRefreshing,
    refresh,
  } = useActivity();
  const isCompact = useResponsive(BREAKPOINTS.tablet);

  const isAvailable = section.state === "available";
  const hasRecords = records.length > 0;
  const isEmpty = isAvailable && !hasRecords;
  const refreshLabel = isRefreshing ? "Refreshing…" : "Refresh";
  const countLabel = isAvailable
    ? `${plural(total, "event")} · Lisbon time`
    : null;

  return (
    <PageContent>
      <PageHeader>
        <PageTitle>
          <PageHeading>Activity</PageHeading>
          <Caption>Every admin action, newest first.</Caption>
          {isAvailable && <ObservedAt observedAt={section.observedAt} />}
        </PageTitle>
        <Button
          type={ButtonTypes.cancel}
          size="small"
          disabled={isRefreshing}
          onClick={refresh}
        >
          {refreshLabel}
        </Button>
      </PageHeader>
      <ActivityToolbar
        query={query}
        action={action}
        period={period}
        isFiltered={isFiltered}
        countLabel={countLabel}
        onQueryChange={changeQuery}
        onActionChange={changeAction}
        onPeriodChange={changePeriod}
        onClearFilters={clearFilters}
      />
      {section.state === "loading" && (
        <Skeleton variant="rectangular" height="12rem" />
      )}
      {section.state === "unavailable" && (
        <Card aria-label="Activity">
          <SectionUnavailable
            observedAt={section.observedAt}
            onRetry={refresh}
          />
        </Card>
      )}
      {isAvailable && section.isRefreshFailed && (
        <RefreshFailedNotice sectionName="Activity" onRetry={refresh} />
      )}
      {isEmpty && isFiltered && (
        <EmptyState
          title="No activity matches these filters"
          description="Try another symbol, admin or a wider date range."
          action={
            <Button
              type={ButtonTypes.cancel}
              size="small"
              onClick={clearFilters}
            >
              Clear filters
            </Button>
          }
        />
      )}
      {isEmpty && !isFiltered && (
        <EmptyState
          title="No admin activity yet"
          description="Reviews, instrument fixes and job recoveries will appear here once an admin makes them."
        />
      )}
      {hasRecords && (
        <Card>
          <ActivityList records={records} />
          <ActivityPager
            page={page}
            pageSize={pageSize}
            shownCount={records.length}
            total={total}
            isCompact={isCompact}
            onPageChange={changePage}
            onPageSizeChange={changePageSize}
          />
        </Card>
      )}
    </PageContent>
  );
};
